<template>
  <div class="relative">
    <!-- Icon (left) - centered vertically -->
    <div v-if="$slots.icon || icon" class="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
      <slot name="icon">
        <component :is="icon" v-if="icon" class="w-5 h-5 text-gray-400" />
      </slot>
    </div>

    <!-- Input Element -->
    <input
      :id="id"
      :type="type"
      :value="value"
      :placeholder="placeholder"
      :disabled="disabled"
      :required="required"
      :autocomplete="autocomplete"
      :class="inputClasses"
      @input="$emit('input', $event.target.value)"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    >

    <!-- Suffix Icon (right) -->
    <div v-if="$slots.suffix" class="absolute inset-y-0 right-0 pr-4 flex items-center">
      <slot name="suffix" />
    </div>
  </div>
</template>

<script>
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const inputVariants = cva(
  // Base styles - flat design with clean borders, appearance-none removes browser 3D styling
  // IMPORTANT: text-gray-900 ensures dark text on white background (fixes white-on-white issue)
  'w-full appearance-none transition-colors duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-900 placeholder-gray-400',
  {
    variants: {
      variant: {
        default: 'border border-gray-300 focus:border-gray-400',
        error: 'border border-red-500 focus:border-red-600',
        success: 'border border-green-500 focus:border-green-600',
      },
      size: {
        sm: 'px-3 py-2.5 text-base rounded-lg min-h-[44px]',
        md: 'px-4 py-3 text-base rounded-lg min-h-[44px]',
        lg: 'px-5 py-3.5 text-base sm:text-lg rounded-xl min-h-[48px]',
      },
      hasIcon: {
        true: '',
        false: ''
      }
    },
    compoundVariants: [
      {
        size: 'sm',
        hasIcon: true,
        class: 'pl-10'
      },
      {
        size: 'md',
        hasIcon: true,
        class: 'pl-12'
      },
      {
        size: 'lg',
        hasIcon: true,
        class: 'pl-14'
      }
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
      hasIcon: false
    }
  }
)

export default {
  name: 'Input',
  // Vue 2.7 model option - use 'value' prop and 'input' event for v-model compatibility
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    id: {
      type: String,
      default: null
    },
    type: {
      type: String,
      default: 'text'
    },
    value: {
      type: [String, Number],
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: String,
      default: null
    },
    variant: {
      type: String,
      default: 'default',
      validator: value => ['default', 'error', 'success'].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: value => ['sm', 'md', 'lg'].includes(value)
    },
    icon: {
      type: [String, Object],
      default: null
    },
    customClass: {
      type: String,
      default: ''
    }
  },
  emits: ['input', 'blur', 'focus'],
  computed: {
    hasIcon() {
      return !!(this.$slots.icon || this.icon)
    },
    inputClasses() {
      return cn(
        inputVariants({
          variant: this.variant,
          size: this.size,
          hasIcon: this.hasIcon
        }),
        this.customClass
      )
    }
  }
}
</script>

<style scoped>
/* Ensure flat styling across all browsers */
input {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  box-shadow: none !important;
}
</style>
